import React, { useEffect, useRef, useState } from 'react';
import {
  bool, func, instanceOf, node, number, oneOf, oneOfType, shape, string
} from 'prop-types';
import classNames from 'classnames/bind';
import {
  applyInlineOffsetMargin,
  getScrollPosition,
  getTopPosition,
  isRefInViewport,
  isScrolledPastContent,
  isScrolledPastOffset
} from '../helpers';
import styles from './sticky.module.scss';

const cx = classNames.bind(styles);

const Sticky = ({
  animation,
  boxShadow,
  children,
  className,
  forwardRef,
  offsetMargin,
  offsetScroll,
  position,
  stickyType,
  stickyByDefault,
  visibleByDefault
}) => {
  const [isSticky, setIsSticky] = useState(stickyByDefault);
  const [isVisible, setIsVisible] = useState(visibleByDefault);
  const [offsetMarginValue, setOffsetMarginValue] = useState(offsetMargin);

  const stickyRef = useRef(null);
  const contentTopPosition = useRef({ top: 0 });
  const lastScrollPosition = useRef({ last: 0 });

  const shouldDisplayBasic = () => {
    const shouldDisplay = isScrolledPastOffset(offsetScroll, window);
    if (isVisible !== shouldDisplay) {
      setIsVisible(shouldDisplay);
    }
  };

  const shouldDisplayRef = () => {
    const { offsetHeight } = forwardRef?.current;
    const forwardRefRect = forwardRef?.current.getBoundingClientRect();
    const stickyRefRect = stickyRef?.current.getBoundingClientRect();

    const isForwardRefInViewport = isRefInViewport({
      offsetScroll,
      refHeight: offsetHeight,
      refRect: forwardRefRect,
      windowObj: window
    });

    if (isVisible !== !isForwardRefInViewport) {
      setIsVisible(!isForwardRefInViewport);
    }
  };

  const shouldDisplayOnScrollUp = () => {
    // saves top position of the content to be made sticky
    const { top } = contentTopPosition?.current || {};
    if (!top) contentTopPosition.current.top = getTopPosition(stickyRef);

    // get current scroll position
    const scrollPosition = getScrollPosition(window);

    const didPassDown = isScrolledPastContent(window, top);
    if (didPassDown) {
      const isScrollingDown = scrollPosition > lastScrollPosition.current.last;
      if (isScrollingDown) {
        // hide content
        setOffsetMarginValue(0);
        setIsVisible(false);
      } else {
        // show content
        setOffsetMarginValue(offsetMargin);
        setIsSticky(true);
        setIsVisible(true);
      }
    } else {
      // un-stick content
      setIsSticky(false);
      setIsVisible(true);
    }
    lastScrollPosition.current.last = scrollPosition <= 0 ? 0 : scrollPosition;
  };

  const handleScroll = () => {
    switch (stickyType) {
    case 'basic':
      shouldDisplayBasic();
      break;
    case 'forwardRef':
      shouldDisplayRef();
      break;
    case 'showOnScrollUp':
      shouldDisplayOnScrollUp();
      break;
    default:
      shouldDisplayBasic();
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isSticky, isVisible]);

  // adds an offset style while showing and hiding with slide animation
  const stickyStyles = applyInlineOffsetMargin(animation, offsetMarginValue, position, stickyType);

  const stickyClasses = classNames(className, {
    [cx('sticky--bottom')]: position === 'bottom',
    [cx('sticky--top')]: position === 'top',
    [cx('sticky__shadow--bottom')]: boxShadow && position === 'bottom',
    [cx('sticky__shadow--top')]: boxShadow && position === 'top',
    [cx('sticky--fixed')]: isSticky,
    // makes the content to slide in from top/bottom while showing and hiding
    [cx('sticky__slide--fixed')]: isSticky && animation === 'slide',
    [cx('sticky__slide--bottom-hide')]: !isVisible && animation === 'slide' && position === 'bottom',
    [cx('sticky__slide--top-hide')]: !isVisible && animation === 'slide' && position === 'top',
    // makes the content to zoom/scale while showing and hiding
    [cx('sticky__scale--fixed')]: isSticky && animation === 'scale',
    [cx('sticky__scale--show')]: isSticky && animation === 'scale',
    [cx('sticky__scale--hide')]: !isVisible && animation === 'scale',
    // makes the content to show and hide by just using display property
    [cx('sticky__display')]: !isVisible && !animation,
    [cx('sticky__display--visible')]: isVisible && !animation,
  });

  return (
    <div className={stickyClasses} ref={stickyRef} style={stickyStyles}>
      {children}
    </div>
  );
};

Sticky.displayName = 'Sticky';

Sticky.propTypes = {
  animation: oneOf(['scale', 'slide']),
  boxShadow: bool,
  children: node,
  className: string,
  // avoid propType error by allowing undefined prop type on ssr only
  forwardRef: oneOfType([
    func,
    shape({ current:
      instanceOf(
        typeof Element === 'undefined'
          ? function () { }
          : Element
      ) })
  ]),
  offsetMargin: number,
  offsetScroll: number,
  position: oneOf(['bottom', 'top']).isRequired,
  stickyByDefault: bool,
  stickyType: oneOf(['basic', 'forwardRef', 'showOnScrollUp']),
  visibleByDefault: bool
};

Sticky.defaultProps = {
  animation: null,
  boxShadow: false,
  children: null,
  className: '',
  forwardRef: null,
  offsetMargin: 0,
  offsetScroll: 0,
  stickyByDefault: false,
  stickyType: 'basic',
  visibleByDefault: false
};

export { Sticky };
